import { Box, Container, Typography } from '@mui/material';

import { MODEL_VERSION_OPTIONS, ModelVersion } from '@verantos-dev/app-state-research-assistant';
import { Button, Dropdown } from '@verantos-dev/shared-components';
import { getSmoothWidthScaling } from '@verantos-dev/shared-utils';

import { DotCountMessage } from './DotCountMessage';
import { useResearchAssistantChatWindow } from './useResearchAssistantChatWindow';
import { ChatWindowUserInputField } from '../ChatWindowUserInputField/ChatWindowUserInputField';
import { ResearchAssistantMessage } from '../ResearchAssistantMessage/ResearchAssistantMessage';

export interface ResearchAssistantChatWindowProps {
  toggleAssistant?: () => void;
  allowMessageSubmit: boolean;
  isOwner?: boolean;
}

export const ResearchAssistantChatWindow = ({
  toggleAssistant,
  allowMessageSubmit,
  isOwner,
}: ResearchAssistantChatWindowProps) => {
  const {
    intl,
    chatWindowRef,
    theme,
    model,
    onModelChange,
    userPrompt,
    onUserPromptChange,
    sendUserPrompt,
    messages,
    isWaitingForResponse,
    isReady,
    messagesEndRef,
    shouldShowVersionSelector,
  } = useResearchAssistantChatWindow();

  return (
    <Container
      sx={{
        display: 'flex',
        flex: 2,
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        minWidth: '448px',
        width: getSmoothWidthScaling(448),
        height: '100%',
        borderRadius: '8px',
        border: `1px solid ${theme.palette.divider}`,
        '&.MuiContainer-root': {
          '@media (min-width: 600px)': {
            padding: '0',
          },
          padding: '0',
        },
      }}
    >
      {/* Header */}
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
          width: '100%',
          padding: `${theme.spacing(4)} ${theme.spacing(6)} !important`,
        }}
      >
        {toggleAssistant && (
          <Button
            displayLabel="Hide Research Assistant"
            id="toggle-assistant-button"
            onClickHandler={toggleAssistant}
          />
        )}
        <Typography variant="h6">
          {intl.formatMessage({
            id: 'projects.researchAssistant.chatWindow.title',
          })}
        </Typography>

        {shouldShowVersionSelector && (
          <Dropdown
            id="model-dropdown"
            data-testid="model-dropdown"
            viewMode={!allowMessageSubmit}
            disabled={!allowMessageSubmit}
            readOnly={MODEL_VERSION_OPTIONS.length === 1}
            label={intl.formatMessage({
              id: 'projects.researchAssistant.chatWindow.modelDropdown.label',
            })}
            options={MODEL_VERSION_OPTIONS}
            value={model}
            onChange={(event) => onModelChange(event.target.value as ModelVersion)}
            size="small"
            width={85}
          />
        )}
      </Container>

      {/* Message Content */}
      <Container
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          flexGrow: 1,
          padding: `${theme.spacing(1)} ${theme.spacing(6)} !important`,
          overflow: 'auto',
        }}
      >
        <Box sx={{ overflow: 'auto', flexGrow: 1, width: '100%' }} ref={chatWindowRef}>
          {messages?.map((message, i) => (
            <ResearchAssistantMessage key={i} {...message} />
          ))}
          {isWaitingForResponse && messages?.length && (
            <ResearchAssistantMessage
              message={<DotCountMessage />}
              currentUser="research_assistant"
              isCurrentUser={false}
            />
          )}
          <div ref={messagesEndRef} />
        </Box>
      </Container>

      {/* Input Field */}
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          padding: `0 ${theme.spacing(6)} !important`,
        }}
      >
        {/* Input Field */}
        <ChatWindowUserInputField
          onTextChange={onUserPromptChange}
          text={userPrompt}
          onSendMessage={sendUserPrompt}
          isOwner={!!isOwner}
          allowMessageSubmit={!!allowMessageSubmit}
          isWaitingForResponse={!!isWaitingForResponse}
          isRaReady={!!isReady}
        />
      </Container>

      {/* Footer */}
      <Container
        sx={{ marginBottom: theme.spacing(4), padding: `0 ${theme.spacing(6)} !important` }}
      >
        <Typography variant="caption">
          {intl.formatMessage({ id: 'projects.researchAssistant.footer.disclaimer' })}
        </Typography>
      </Container>
    </Container>
  );
};
